// ═══════════════════════════════════════════════════════════════
// XYQAR — Exec Devis (Quotes) — create & manage commercial quotes
// ═══════════════════════════════════════════════════════════════
import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, apiPost } from '../lib/api';
import {
  PageTitle, Section, Kpi, Btn, Table, Th, Td, fmt, EmptyState,
} from '../components/ui-kit';
import { Plus, FileText, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Devis {
  id:           string;
  number:       string;   // backend field (DEV-YYYYMM-XXXX)
  company_id:   string;
  company_name: string;
  pack_level:   string;
  geo_zone:     string;
  offer_type:   string;
  amount_ht:    number;   // excl. VAT
  amount_ttc:   number;   // incl. VAT
  status:       string;
  created_at:   string;
}

const GEO_LABELS: Record<string, string> = {
  FR: 'France', UK: 'United Kingdom', CH: 'Switzerland',
  DE: 'Germany', US: 'USA', AE: 'UAE',
};

const PACK_LABELS: Record<string, string> = {
  L1: 'L1 — Access', L2: 'L2 — Signature', L3: 'L3 — Sovereign',
};

export default function Devis() {
  const { getToken } = useXyqarAuth();
  const nav = useNavigate();

  const [items,      setItems]      = React.useState<Devis[] | null>(null);
  const [showForm,   setShowForm]   = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [msg,        setMsg]        = React.useState<string | null>(null);

  const [form, setForm] = React.useState({
    company_id:   '',
    pack_level:   'L1',
    geo_zone:     'FR',
    offer_type:   'on_demand',
    mission_desc: '',
    amount_eur:   '',
  });

  const load = React.useCallback(async () => {
    try {
      const tok = await getToken().catch(() => '');
      const j = await apiGet<{ devis: Devis[] }>('/api/v1/exec/devis', tok);
      setItems(j.devis ?? []);
    } catch { setItems([]); }
  }, [getToken]);

  React.useEffect(() => { load(); }, [load]);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.company_id.trim()) { setMsg('Company name is required.'); return; }
    setSubmitting(true); setMsg(null);
    try {
      const tok = await getToken().catch(() => '');
      const j = await apiPost<{ id: string; number: string }>(
        '/api/v1/exec/devis',
        { ...form, amount_eur: Number(form.amount_eur) || 0 },
        tok,
      );
      setShowForm(false);
      setForm({ company_id: '', pack_level: 'L1', geo_zone: 'FR', offer_type: 'on_demand', mission_desc: '', amount_eur: '' });
      await load();
      setMsg(`Devis ${j.number} created — status: draft. Approve it to convert to contract.`);
    } catch (e: any) {
      setMsg(e.message ?? 'Could not create quote');
    } finally {
      setSubmitting(false);
    }
  };

  const approveDevis = async (devisId: string) => {
    try {
      const tok = await getToken().catch(() => '');
      await apiPost(`/api/v1/exec/devis/${devisId}/approve`, {}, tok);
      await load();
      setMsg('Devis approved — you can now convert it to a contract.');
    } catch (e: any) {
      setMsg(e.message ?? 'Failed to approve devis');
    }
  };

  const total    = (items ?? []).reduce((s, d) => s + (d.amount_ht ?? 0), 0);
  const approved = (items ?? []).filter((d) => d.status === 'approved').length;
  const pending  = (items ?? []).filter((d) => d.status === 'draft' || d.status === 'pending').length;

  const inp = 'w-full rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40';
  const sel = inp + ' cursor-pointer';

  return (
    <div>
      <PageTitle
        kicker="Revenue architecture"
        title="Quotes and commercial lanes"
        right={
          <Btn primary onClick={() => setShowForm((v) => !v)}>
            <span className="inline-flex items-center gap-2">
              <Plus className="h-4 w-4" />
              {showForm ? 'Cancel' : 'New quote'}
            </span>
          </Btn>
        }
      />

      {msg && (
        <div className="mb-6 rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white/80 font-mono">
          {msg}
        </div>
      )}

      {showForm && (
        <Section title="New quote" hint="Scope a mandate into the right Xyqar lane before legal conversion">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Client / Company name *</label>
              <input className={inp} placeholder="Acme Corp" value={form.company_id} onChange={(e) => set('company_id', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Mission brief</label>
              <input className={inp} placeholder="Short description…" value={form.mission_desc} onChange={(e) => set('mission_desc', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Commercial lane</label>
              <select className={sel} value={form.pack_level} onChange={(e) => set('pack_level', e.target.value)}>
                {Object.entries(PACK_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Geography</label>
              <select className={sel} value={form.geo_zone} onChange={(e) => set('geo_zone', e.target.value)}>
                {Object.entries(GEO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Execution model</label>
              <select className={sel} value={form.offer_type} onChange={(e) => set('offer_type', e.target.value)}>
                <option value="on_demand">On-Demand</option>
                <option value="emergency">Emergency</option>
                <option value="pool">Dedicated pool</option>
                <option value="enterprise">Enterprise frame</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Amount (EUR, excl. VAT)</label>
              <input type="number" className={inp} placeholder="0" value={form.amount_eur} onChange={(e) => set('amount_eur', e.target.value)} />
            </div>
          </div>
          <Btn primary onClick={submit} disabled={submitting}>
            <span className="inline-flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {submitting ? 'Creating…' : 'Create quote'}
            </span>
          </Btn>
        </Section>
      )}

      <Section title="At a glance" hint="Track what is draft, approved, and ready for legal conversion">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Total quotes"   value={fmt(items?.length)} />
          <Kpi accent="green" label="Approved"  value={fmt(approved)} />
          <Kpi accent="amber" label="Pending"   value={fmt(pending)} />
          <Kpi label="Pipeline €"    value={fmt(Math.round(total))} hint="excl. VAT" />
        </div>
      </Section>

      <Section title="Quotes" hint="Commercial record before contract, signature, and provisioning">
        {items && items.length === 0 && <EmptyState title="No quotes yet" hint="Create the first quote above to start the commercial pipeline." />}
        {(items ?? []).length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Client</Th>
                <Th>Pack</Th>
                <Th>Geo</Th>
                <Th>Amount €</Th>
                <Th>Status</Th>
                <Th>Date</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {(items ?? []).map((d) => (
                <tr key={d.id}>
                  <Td className="font-mono text-xs">{d.number}</Td>
                  <Td>{d.company_name || d.company_id}</Td>
                  <Td>{d.pack_level}</Td>
                  <Td>{GEO_LABELS[d.geo_zone] ?? d.geo_zone}</Td>
                  <Td>{fmt(Math.round(d.amount_ht))}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${
                      d.status === 'approved' ? 'border-emerald-700/40 text-emerald-400 bg-emerald-900/20' :
                      d.status === 'pending'  ? 'border-amber-700/40 text-amber-400 bg-amber-900/20' :
                      'border-white/10 text-white/50'
                    }`}>{d.status}</span>
                  </Td>
                  <Td className="text-white/40 text-xs">{new Date(d.created_at).toLocaleDateString('fr-FR')}</Td>
                  <Td>
                    {(d.status === 'draft' || d.status === 'pending') && (
                      <button
                        onClick={() => approveDevis(d.id)}
                        className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-200 transition mr-2"
                        title="Approve this quote"
                      >
                        Approve
                      </button>
                    )}
                    {d.status === 'approved' && (
                      <button
                        onClick={async () => {
                          try {
                            const tok = await getToken().catch(() => '');
                            const j = await apiPost<{ contract_id: string }>(`/api/v1/exec/contracts`, { devis_id: d.id }, tok);
                            nav(`/contracts/${j.contract_id}/review`);
                          } catch (e: any) { setMsg(e.message ?? 'Failed to create contract'); }
                        }}
                        className="flex items-center gap-1 text-xs text-white/60 hover:text-white transition"
                        title="Convert to contract"
                      >
                        Contract <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </div>
  );
}
